import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { FeedbackProvider } from './contexts/FeedbackContext';
import { Button, Modal } from './components/common/UI';

// Layouts
import MainLayout from './components/layout/MainLayout';
import AuthLayout from './components/layout/AuthLayout';

// Auth Pages
import LoginPage from './pages/auth/LoginPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import LocationPage from './pages/onboarding/LocationPage';
import PlanSelectionPage from './pages/onboarding/PlanSelectionPage';
import LandingPage from './pages/public/LandingPage';
import TermsPage from './pages/public/TermsPage';

// Business Pages
import BusinessListPage from './pages/business/BusinessListPage';
import BusinessDetailPage from './pages/business/BusinessDetailPage';
import BusinessFormPage from './pages/business/BusinessFormPage';
import DashboardPage from './pages/dashboard/DashboardPage';

// Client Pages
import ClientListPage from './pages/clients/ClientListPage';
import ClientFormPage from './pages/clients/ClientFormPage';
import ClientDetailPage from './pages/clients/ClientDetailPage';

// Visualization Pages
import VisualizationPage from './pages/visualization/VisualizationPage';

// Payments
import SubscriptionPage from './pages/payments/SubscriptionPage';

// Profile
import ProfilePage from './pages/auth/ProfilePage';

// Admin
import AdminPage from './pages/admin/AdminPage';

// ── Protected Route Guard ─────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="loading-spinner">Carregando...</div>;
  return user ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== 'admin') return <AdminAccessDenied />;
  return children;
};

const AdminAccessDenied = () => {
  const { t } = useTranslation();

  return (
    <Modal isOpen title={t('admin.accessDeniedTitle')} onClose={() => { window.location.href = '/dashboard'; }} size="sm">
      <p className="mb-5 text-sm leading-6 text-slate-600">{t('admin.accessDeniedMessage')}</p>
      <Button type="button" onClick={() => { window.location.href = '/dashboard'; }} fullWidth>
        {t('admin.backToDashboard')}
      </Button>
    </Modal>
  );
};

// ── App ────────────────────────────────────────────────────────
const PaymentsRedirect = () => {
  const location = useLocation();
  return <Navigate to={`/subscription${location.search}`} replace />;
};

const App = () => (
  <BrowserRouter>
    <AuthProvider>
      <FeedbackProvider>
        <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<TermsPage />} />
        {/* Public auth routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
        </Route>
        <Route path="/localidade" element={<LocationPage />} />
        <Route path="/plans" element={<PlanSelectionPage />} />

        {/* Protected app routes */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/businesses" element={<BusinessListPage />} />
          <Route path="/businesses/new" element={<BusinessFormPage />} />
          <Route path="/businesses/:id" element={<BusinessDetailPage />} />
          <Route path="/businesses/:id/edit" element={<BusinessFormPage />} />
          <Route path="/clients" element={<ClientListPage />} />
          <Route path="/clients/new" element={<ClientFormPage />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />
          <Route path="/clients/:id/edit" element={<ClientFormPage />} />
          <Route path="/visualization" element={<VisualizationPage />} />
          <Route path="/subscription" element={<SubscriptionPage />} />
          <Route path="/payments" element={<PaymentsRedirect />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route
            path="/admin"
            element={
              <AdminRoute>
                <AdminPage />
              </AdminRoute>
            }
          />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </FeedbackProvider>
    </AuthProvider>
  </BrowserRouter>
);

export default App;
